/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Box, Grid, Typography } from '@mui/material'
import React from 'react'
import SingleSelect from '../../components/SingleSelect'
import {
    ACCENT,
    BRAND,
    CONTROL,
    ICON,
    RADIUS,
    SURFACE,
    TEXT,
    tint,
} from '../../theme/tokens'
import { CHANNELS, FieldLabel, SectionLabel } from './wizardShared'

const ChannelChip = ({ channel, selected, onToggle }) => (
    <Box
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-label={`${channel.label} channel`}
        onClick={onToggle}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle()
            }
        }}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            py: 0.9,
            borderRadius: `${RADIUS.pill}px`,
            cursor: 'pointer',
            outline: 'none',
            border: `1.5px solid ${
                selected ? ACCENT.border55 : SURFACE.border
            }`,
            background: selected ? ACCENT.bg08 : SURFACE.subtle,
            color: selected ? channel.color : TEXT.faint,
            transition: 'all 0.15s',
            userSelect: 'none',
            '&:hover': {
                borderColor: selected
                    ? tint(BRAND.orange, 0.8)
                    : SURFACE.borderHover,
                background: selected ? ACCENT.bg12 : SURFACE.hover,
            },
            '&:focus-visible': {
                boxShadow: `0 0 0 2px ${CONTROL.focusRing}`,
            },
        }}
    >
        {channel.icon}
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {channel.label}
        </Typography>
        {selected && (
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: channel.color,
                    ml: 0.25,
                }}
            />
        )}
    </Box>
)

/**
 * Step 2: pick delivery channels and the audience (receiver type).
 * `singleChannel` (edit mode): an existing pipeline sends on exactly one
 * channel, so picking a channel replaces it instead of adding one.
 * `receiverOptions`: the receiver types to offer (the service's list).
 * `receiverLockReason`: set when the receiver type is fixed (an existing
 * event type's receiver, or a holiday's Inline audience); the select is then
 * disabled and the reason shown under it.
 */
const StepChannels = ({
    form,
    onToggleChannel,
    onReceiverChange,
    singleChannel = false,
    receiverOptions,
    receiverLockReason = '',
}) => (
    <Box>
        <SectionLabel>Delivery</SectionLabel>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FieldLabel>Channels</FieldLabel>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {CHANNELS.map((channel) => (
                        <ChannelChip
                            key={channel.value}
                            channel={channel}
                            selected={form.comms.some(
                                (c) => c.comm_type === channel.value
                            )}
                            onToggle={() => onToggleChannel(channel.value)}
                        />
                    ))}
                </Box>
                {form.comms.length === 0 && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: TEXT.faint, mt: 0.75 }}
                    >
                        {singleChannel
                            ? 'Select the channel to continue'
                            : 'Select one or more channels to continue'}
                    </Typography>
                )}
                {singleChannel && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: TEXT.muted, mt: 0.75 }}
                    >
                        A pipeline sends on one channel. To add another channel
                        for this event, create a new pipeline.
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12} sm={6}>
                <FieldLabel>Receiver Type</FieldLabel>
                <SingleSelect
                    label=""
                    ariaLabel="Receiver type"
                    options={receiverOptions}
                    value={form.receiverType}
                    optionValueText="value"
                    optionLabelText="label"
                    handleChange={onReceiverChange}
                    disabled={!!receiverLockReason}
                />
                {receiverLockReason && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            mt: 0.75,
                        }}
                    >
                        <LockOutlinedIcon
                            sx={{
                                fontSize: ICON.sm,
                                color: TEXT.muted,
                                mt: '2px',
                            }}
                        />
                        <Typography
                            data-testid="receiver-lock-reason"
                            variant="caption"
                            sx={{ color: TEXT.muted }}
                        >
                            {receiverLockReason}
                        </Typography>
                    </Box>
                )}
            </Grid>
        </Grid>
    </Box>
)

export default StepChannels
